/**
 * @fileoverview Client Change Orders page component
 *
 * Displays change order statistics and a list of change orders for the client's project.
 * Features include:
 * - Summary statistics cards (Total, Approved, Pending, Cost Impact, Time Impact)
 * - Collapsible change order cards with status indicators
 * - Attachment thumbnails with lightbox viewer
 * - PDF modal viewer with download button
 * - Impact Summary section for approved/completed orders
 * - Responsive design for mobile and desktop
 *
 * Field Mapping Notes:
 * - "Change Order Title" field → title/name display
 * - "Project CO Number" field → CO number badge
 * - "Official/Signed Documentation" → signed PDFs
 * - "Files & Photos" (linked) → related files
 * - "Other Files" (attachment) → additional attachments
 * - "Denial Reason" → denialReason for denied orders
 *
 * @requires react
 * @requires react-icons/fa
 * @requires ../firebase
 * @requires ../hooks/useProjects
 * @requires ../hooks/useChangeOrders
 * @requires ../utils/dateUtils
 */

import React, { useState } from "react";
import { useClientProject } from "../../hooks/useProjects";
import { useChangeOrders } from "../../hooks/useChangeOrders";
import { formatDateET } from "../../utils/dateUtils";
import {
  FaFileAlt,
  FaCheckCircle,
  FaClock,
  FaDollarSign,
  FaCalendarAlt,
  FaTimesCircle,
  FaTimes,
  FaChevronLeft,
  FaChevronRight,
  FaChevronDown,
  FaChevronUp,
  FaDownload,
  FaExpand,
  FaFilePdf,
  FaExclamationTriangle,
} from "react-icons/fa";
import "./ClientChangeOrders.css";

/**
 * ClientChangeOrders Component
 * Main component for displaying change order information
 *
 * @returns {JSX.Element} The change orders page
 */
function ClientChangeOrders() {
  const [expandedCards, setExpandedCards] = useState({});
  const [lightboxImages, setLightboxImages] = useState([]);
  const [lightboxIndex, setLightboxIndex] = useState(0);
  const [pdfModal, setPdfModal] = useState(null);

  // Fetch project data
  const {
    data: projectResponse,
    isLoading: projectLoading,
    error: projectError,
  } = useClientProject();
  const project = projectResponse?.data || projectResponse;

  // Fetch change orders data
  const {
    data: changeOrders = [],
    isLoading: ordersLoading,
    error: ordersError,
  } = useChangeOrders(project?.id);

  // Combined loading and error states
  const isLoading = projectLoading || ordersLoading;
  const error = projectError || ordersError;

  /**
   * Toggle card expansion state
   * @param {string} cardId - ID of the card to toggle
   */
  const toggleCard = (cardId) => {
    setExpandedCards((prev) => ({
      ...prev,
      [cardId]: !prev[cardId],
    }));
  };

  /**
   * Calculate summary statistics from change orders
   * @returns {Object} Statistics object
   */
  const calculateStats = () => {
    const stats = {
      total: changeOrders.length,
      approved: changeOrders.filter(
        (co) => co.status === "Approved" || co.status === "Completed",
      ).length,
      pending: changeOrders.filter(
        (co) => co.status === "Pending" || co.status === "Submitted",
      ).length,
      denied: changeOrders.filter((co) => co.status === "Denied").length,
      totalCostImpact: changeOrders
        .filter((co) => co.status === "Approved" || co.status === "Completed")
        .reduce((sum, co) => sum + (co.costImpact || 0), 0),
      totalTimeImpact: changeOrders
        .filter((co) => co.status === "Approved" || co.status === "Completed")
        .reduce((sum, co) => sum + (co.timeImpact || 0), 0),
    };
    return stats;
  };

  /**
   * Get approved/completed change orders for Impact Summary
   * @returns {Array} Filtered change orders
   */
  const getApprovedOrders = () => {
    return changeOrders.filter(
      (co) => co.status === "Approved" || co.status === "Completed",
    );
  };

  const stats = calculateStats();
  const approvedOrders = getApprovedOrders();

  /**
   * Format currency for display
   * @param {number} amount - Amount to format
   * @param {boolean} showSign - Whether to show +/- sign
   * @returns {string} Formatted currency string
   */
  const formatCurrency = (amount, showSign = false) => {
    const absAmount = Math.abs(amount);
    const formatted = new Intl.NumberFormat("en-US", {
      style: "currency",
      currency: "USD",
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    }).format(absAmount);

    if (showSign && amount > 0) {
      return `+${formatted}`;
    } else if (amount < 0) {
      return `-${formatted}`;
    }
    return formatted;
  };

  /**
   * Format date for display using Eastern Time
   * @param {string} dateString - ISO date string
   * @returns {string} Formatted date
   */
  const formatDate = (dateString) => {
    if (!dateString) return "—";
    return formatDateET(dateString);
  };

  /**
   * Get status badge configuration
   * @param {string} status - Change order status
   * @returns {Object} Badge configuration with icon and class
   */
  const getStatusConfig = (status) => {
    switch (status) {
      case "Approved":
        return { icon: <FaCheckCircle />, className: "co-status-approved" };
      case "Completed":
        return { icon: <FaCheckCircle />, className: "co-status-completed" };
      case "Pending":
      case "Submitted":
        return { icon: <FaClock />, className: "co-status-pending" };
      case "Denied":
        return { icon: <FaTimesCircle />, className: "co-status-denied" };
      default:
        return { icon: <FaClock />, className: "co-status-pending" };
    }
  };

  /**
   * Combine all attachments from different sources
   * @param {Object} co - Change order object
   * @returns {Array} Combined attachments array
   */
  const getAllAttachments = (co) => {
    const attachments = [];

    // Add signed documentation
    if (co.signedDocumentation?.length > 0) {
      co.signedDocumentation.forEach((att) => {
        attachments.push({ ...att, source: "signed" });
      });
    }

    // Add files & photos
    if (co.filesPhotos?.length > 0) {
      co.filesPhotos.forEach((att) => {
        attachments.push({ ...att, source: "filesPhotos" });
      });
    }

    // Add other files
    if (co.otherFiles?.length > 0) {
      co.otherFiles.forEach((att) => {
        attachments.push({ ...att, source: "other" });
      });
    }

    return attachments;
  };

  /**
   * Check if attachment is an image
   * @param {Object} att - Attachment object
   * @returns {boolean} True if image
   */
  const isImage = (att) => {
    return (
      att.type?.startsWith("image/") ||
      att.filename?.match(/\.(jpg|jpeg|png|gif|webp)$/i)
    );
  };

  /**
   * Check if attachment is a PDF
   * @param {Object} att - Attachment object
   * @returns {boolean} True if PDF
   */
  const isPDF = (att) => {
    return att.type === "application/pdf" || att.filename?.match(/\.pdf$/i);
  };

  /**
   * Open lightbox for image attachments
   * @param {Array} attachments - Array of attachment objects
   * @param {number} startIndex - Index to start at
   */
  const openLightbox = (attachments, startIndex = 0) => {
    const images = attachments.filter((att) => isImage(att));
    if (images.length > 0) {
      setLightboxImages(images);
      setLightboxIndex(startIndex);
    }
  };

  /**
   * Close the lightbox
   */
  const closeLightbox = () => {
    setLightboxImages([]);
    setLightboxIndex(0);
  };

  /**
   * Navigate lightbox images
   * @param {number} direction - 1 for next, -1 for previous
   */
  const navigateLightbox = (direction) => {
    const newIndex = lightboxIndex + direction;
    if (newIndex >= 0 && newIndex < lightboxImages.length) {
      setLightboxIndex(newIndex);
    }
  };

  /**
   * Open PDF modal viewer
   * @param {Object} pdf - PDF attachment object
   */
  const openPdfModal = (pdf) => {
    setPdfModal(pdf);
  };

  /**
   * Close PDF modal viewer
   */
  const closePdfModal = () => {
    setPdfModal(null);
  };

  // Loading state
  if (isLoading) {
    return (
      <>
        <div className="component-loading">
          <div className="component-spinner"></div>
          <p>Loading change orders...</p>
        </div>
      </>
    );
  }

  // Error state
  if (error) {
    return (
      <>
        <div className="dashboard-error">
          <FaExclamationTriangle />
          <h2>Unable to load Change Orders</h2>
          <p>{projectError.message}</p>
          <button onClick={() => window.location.reload()}>Try Again</button>
        </div>
      </>
    );
  }

  return (
    <>
      <div className="page-container">
        {/* Page Header */}
        <header className="page-header">
          <h1 className="page-title">Change Orders</h1>
          <p className="page-subtitle">View and Track Project Modifications</p>
        </header>

        {/* Statistics Cards */}
        <section className="co-stats-section">
          <div className="co-stats-grid">
            {/* Total Orders */}
            <div className="co-stat-card">
              <div className="co-stat-icon co-stat-icon-total">
                <FaFileAlt />
              </div>
              <div className="co-stat-content">
                <span className="co-stat-value">{stats.total}</span>
                <span className="co-stat-label">Total Orders</span>
              </div>
            </div>

            {/* Approved */}
            <div className="co-stat-card">
              <div className="co-stat-icon co-stat-icon-approved">
                <FaCheckCircle />
              </div>
              <div className="co-stat-content">
                <span className="co-stat-value">{stats.approved}</span>
                <span className="co-stat-label">Approved</span>
              </div>
            </div>

            {/* Pending */}
            <div className="co-stat-card">
              <div className="co-stat-icon co-stat-icon-pending">
                <FaClock />
              </div>
              <div className="co-stat-content">
                <span className="co-stat-value">{stats.pending}</span>
                <span className="co-stat-label">Pending</span>
              </div>
            </div>

            {/* Cost Impact */}
            <div className="co-stat-card">
              <div className="co-stat-icon co-stat-icon-cost">
                <FaDollarSign />
              </div>
              <div className="co-stat-content">
                <span
                  className={`co-stat-value ${
                    stats.totalCostImpact >= 0
                      ? "co-cost-debit"
                      : "co-cost-credit"
                  }`}
                >
                  {formatCurrency(stats.totalCostImpact, true)}
                </span>
                <span className="co-stat-label">Cost Impact</span>
              </div>
            </div>

            {/* Time Impact */}
            <div className="co-stat-card">
              <div className="co-stat-icon co-stat-icon-time">
                <FaCalendarAlt />
              </div>
              <div className="co-stat-content">
                <span className="co-stat-value">
                  {stats.totalTimeImpact}{" "}
                  <span className="co-stat-value">days</span>
                </span>
                <span className="co-stat-label">Time Impact</span>
              </div>
            </div>
          </div>
        </section>

        {/* Change Orders List */}
        <section className="co-list-section">
          <h2 className="co-section-title">Change Order History</h2>

          {changeOrders.length === 0 ? (
            <div className="co-empty-state">
              <FaFileAlt className="co-empty-icon" />
              <h3 className="co-empty-title">No Change Orders</h3>
              <p className="co-empty-message">
                There are no change orders for your project yet.
              </p>
            </div>
          ) : (
            <div className="co-cards-list">
              {changeOrders.map((co) => {
                const statusConfig = getStatusConfig(co.status);
                const isExpanded = expandedCards[co.id] || false;
                const allAttachments = getAllAttachments(co);

                return (
                  <div
                    key={co.id}
                    className={`co-card ${
                      isExpanded ? "co-card-expanded" : ""
                    }`}
                  >
                    {/* Card Header - Always Visible */}
                    <div
                      className="co-card-header"
                      onClick={() => toggleCard(co.id)}
                    >
                      <div className="co-card-header-content">
                        <div className="co-card-title-row">
                          <span className="co-number">{co.coNumber}</span>
                          <h3 className="co-card-title">{co.title}</h3>
                          <span
                            className={`co-status-badge ${statusConfig.className}`}
                          >
                            {statusConfig.icon}
                            <span>{co.status}</span>
                          </span>
                        </div>
                      </div>
                      <button
                        className="co-expand-btn"
                        aria-label={isExpanded ? "Collapse" : "Expand"}
                      >
                        {isExpanded ? <FaChevronUp /> : <FaChevronDown />}
                      </button>
                    </div>

                    {/* Card Body - Collapsible */}
                    {isExpanded && (
                      <div className="co-card-body">
                        <p className="co-description">{co.description}</p>

                        {/* Details Grid */}
                        <div className="co-details-grid">
                          <div className="co-detail-item">
                            <span className="co-detail-label">Reason</span>
                            <span className="co-detail-value">{co.reason}</span>
                          </div>
                          <div className="co-detail-item">
                            <span className="co-detail-label">
                              Date Submitted
                            </span>
                            <span className="co-detail-value">
                              {formatDate(co.dateSubmitted)}
                            </span>
                          </div>
                          <div className="co-detail-item">
                            <span className="co-detail-label">Cost Impact</span>
                            <span
                              className={`co-detail-value co-cost-value ${
                                co.costImpact >= 0
                                  ? "co-cost-debit"
                                  : "co-cost-credit"
                              }`}
                            >
                              {formatCurrency(co.costImpact, true)}
                            </span>
                          </div>
                          <div className="co-detail-item">
                            <span className="co-detail-label">Time Impact</span>
                            <span className="co-detail-value">
                              {co.timeImpact > 0
                                ? `+${co.timeImpact} days`
                                : co.timeImpact === 0
                                  ? "No impact"
                                  : `${co.timeImpact} days`}
                            </span>
                          </div>
                        </div>

                        {/* Documentation - Combined from all sources */}
                        {allAttachments.length > 0 && (
                          <div className="co-attachments">
                            <span className="co-attachments-label">
                              Documentation
                            </span>
                            <div className="co-attachments-grid">
                              {allAttachments.map((att, idx) => {
                                const imageAttachments = allAttachments.filter(
                                  (a) => isImage(a),
                                );
                                const imageIndex = imageAttachments.findIndex(
                                  (a) => a.id === att.id,
                                );

                                if (isImage(att)) {
                                  return (
                                    <div
                                      key={att.id}
                                      className="co-attachment-item co-attachment-image"
                                      onClick={() =>
                                        openLightbox(allAttachments, imageIndex)
                                      }
                                    >
                                      <div className="co-attachment-thumbnail">
                                        <img src={att.url} alt={att.filename} />
                                        <div className="co-attachment-overlay">
                                          <FaExpand />
                                        </div>
                                      </div>
                                    </div>
                                  );
                                } else if (isPDF(att)) {
                                  return (
                                    <div
                                      key={att.id}
                                      className="co-attachment-item"
                                      onClick={() => openPdfModal(att)}
                                    >
                                      <div className="co-attachment-file co-attachment-pdf">
                                        <FaFilePdf className="co-file-icon" />
                                        <span className="co-file-name">
                                          {att.filename}
                                        </span>
                                        {att.source === "signed" && (
                                          <span className="co-file-badge">
                                            Signed
                                          </span>
                                        )}
                                      </div>
                                    </div>
                                  );
                                } else {
                                  return (
                                    <div
                                      key={att.id}
                                      className="co-attachment-item"
                                    >
                                      <div className="co-attachment-file">
                                        <FaFileAlt className="co-file-icon" />
                                        <span className="co-file-name">
                                          {att.filename}
                                        </span>
                                        <a
                                          href={att.url}
                                          target="_blank"
                                          rel="noopener noreferrer"
                                          className="co-download-btn"
                                          onClick={(e) => e.stopPropagation()}
                                        >
                                          <FaDownload />
                                        </a>
                                      </div>
                                    </div>
                                  );
                                }
                              })}
                            </div>
                          </div>
                        )}

                        {/* Signature Status */}
                        {(co.status === "Approved" ||
                          co.status === "Completed") && (
                          <div
                            className={`co-signature-status ${
                              co.buyerSigned ? "co-signed" : "co-unsigned"
                            }`}
                          >
                            {co.buyerSigned ? (
                              <>
                                <FaCheckCircle />
                                <span>Signed by client</span>
                              </>
                            ) : (
                              <>
                                <FaClock />
                                <span>Awaiting client signature</span>
                              </>
                            )}
                          </div>
                        )}

                        {/* Denial Reason Banner */}
                        {co.status === "Denied" && co.denialReason && (
                          <div className="co-denial-banner">
                            <FaTimesCircle className="co-denial-icon" />
                            <div className="co-denial-content">
                              <span className="co-denial-label">
                                Denial Reason
                              </span>
                              <p className="co-denial-text">
                                {co.denialReason}
                              </p>
                            </div>
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          )}
        </section>

        {/* Impact Summary Section */}
        <section className="co-impact-section">
          <div className="co-impact-card">
            <div className="co-impact-header">
              <FaDollarSign className="co-impact-header-icon" />
              <h2 className="co-impact-title">
                Impact Summary for Approved Changes
              </h2>
            </div>

            {approvedOrders.length === 0 ? (
              <p className="co-impact-empty">
                No approved changes yet. Cost and time impacts from approved
                change orders will be summarized here.
              </p>
            ) : (
              <div className="co-impact-content">
                {/* Cost Impact Column */}
                <div className="co-impact-column">
                  <h3 className="co-impact-column-title">Cost Impact</h3>
                  <div className="co-impact-list">
                    {approvedOrders.map((co) => (
                      <div key={co.id} className="co-impact-row">
                        <span className="co-impact-label">
                          Change Order #{co.coNumber}
                        </span>
                        <span
                          className={`co-impact-value ${
                            co.costImpact >= 0
                              ? "co-cost-debit"
                              : "co-cost-credit"
                          }`}
                        >
                          {formatCurrency(co.costImpact, true)}
                        </span>
                      </div>
                    ))}
                  </div>
                  <div className="co-impact-total">
                    <span className="co-impact-total-label">
                      Total Cost Impact:
                    </span>
                    <span
                      className={`co-impact-total-value ${
                        stats.totalCostImpact >= 0
                          ? "co-cost-debit"
                          : "co-cost-credit"
                      }`}
                    >
                      {formatCurrency(stats.totalCostImpact, true)}
                    </span>
                  </div>
                </div>

                {/* Time Impact Column */}
                <div className="co-impact-column">
                  <h3 className="co-impact-column-title">Time Impact</h3>
                  <div className="co-impact-list">
                    {approvedOrders.map((co) => (
                      <div key={co.id} className="co-impact-row">
                        <span className="co-impact-label">
                          Change Order #{co.coNumber}
                        </span>
                        <span className="co-impact-value co-time-value">
                          {co.timeImpact > 0 ? `${co.timeImpact} days` : "—"}
                        </span>
                      </div>
                    ))}
                  </div>
                  <div className="co-impact-total">
                    <span className="co-impact-total-label">
                      Total Time Impact:
                    </span>
                    <span className="co-impact-total-value co-time-value">
                      {stats.totalTimeImpact} days
                    </span>
                  </div>
                </div>
              </div>
            )}
          </div>
        </section>
      </div>

      {/* Image Lightbox */}
      {lightboxImages.length > 0 && (
        <div className="co-lightbox-overlay" onClick={closeLightbox}>
          <button className="co-lightbox-close" onClick={closeLightbox}>
            <FaTimes />
          </button>

          <div
            className="co-lightbox-content"
            onClick={(e) => e.stopPropagation()}
          >
            <img
              src={lightboxImages[lightboxIndex]?.url}
              alt={lightboxImages[lightboxIndex]?.filename}
            />
          </div>

          {lightboxImages.length > 1 && (
            <>
              <button
                className="co-lightbox-nav co-lightbox-prev"
                onClick={(e) => {
                  e.stopPropagation();
                  navigateLightbox(-1);
                }}
                disabled={lightboxIndex === 0}
              >
                <FaChevronLeft />
              </button>
              <button
                className="co-lightbox-nav co-lightbox-next"
                onClick={(e) => {
                  e.stopPropagation();
                  navigateLightbox(1);
                }}
                disabled={lightboxIndex === lightboxImages.length - 1}
              >
                <FaChevronRight />
              </button>
              <div className="co-lightbox-counter">
                {lightboxIndex + 1} / {lightboxImages.length}
              </div>
            </>
          )}
        </div>
      )}

      {/* PDF Modal Viewer */}
      {pdfModal && (
        <div className="co-pdf-modal-overlay" onClick={closePdfModal}>
          <div className="co-pdf-modal" onClick={(e) => e.stopPropagation()}>
            <div className="co-pdf-modal-header">
              <div className="co-pdf-modal-title">
                <FaFilePdf className="co-pdf-modal-icon" />
                <span>{pdfModal.filename}</span>
              </div>
              <div className="co-pdf-modal-actions">
                <a
                  href={pdfModal.url}
                  download={pdfModal.filename}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="co-pdf-download-btn"
                >
                  <FaDownload />
                  <span>Download</span>
                </a>
                <button className="co-pdf-close-btn" onClick={closePdfModal}>
                  <FaTimes />
                </button>
              </div>
            </div>
            <div className="co-pdf-modal-body">
              <iframe
                src={pdfModal.url}
                title={pdfModal.filename}
                className="co-pdf-viewer"
              />
            </div>
          </div>
        </div>
      )}
    </>
  );
}

export default ClientChangeOrders;
